import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { SxProps, Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import type { Icon } from '@phosphor-icons/react/dist/lib/types'
import { Bank as BankIcon } from '@phosphor-icons/react/dist/ssr/Bank'
import { Bell as BellIcon } from '@phosphor-icons/react/dist/ssr/Bell'
import { Headset as HeadsetIcon } from '@phosphor-icons/react/dist/ssr/Headset'
import { Lockers as LockersIcon } from '@phosphor-icons/react/dist/ssr/Lockers'
import { PlugsConnected as PlugsConnectedIcon } from '@phosphor-icons/react/dist/ssr/PlugsConnected'
import { UserCircle as UserCircleIcon } from '@phosphor-icons/react/dist/ssr/UserCircle'
import { UsersThree as UsersThreeIcon } from '@phosphor-icons/react/dist/ssr/UsersThree'
import { Link as RouterLink } from 'react-router-dom'

import type { NavItemConfig } from '@/types/nav'
import { paths } from '@/paths'
import { isNavItemActive } from '@/lib/is-nav-item-active'
import { useFilteredNavItems } from '@/hooks/use-filtered-nav-items'
import { usePathname } from '@/hooks/use-pathname'
import { useUser } from '@/hooks/use-user'
import { UserAvatar } from '@/components/core/user-avatar'

const navItems = [
	{
		key: 'personal',
		title: 'Personal',
		items: [
			{
				key: 'account',
				title: 'Account',
				href: paths.dashboard.settings.account,
				icon: 'user-circle',
			},
			{
				key: 'notifications',
				title: 'Notifications',
				href: paths.dashboard.settings.notifications,
				icon: 'bell',
				requiresHealthAccess: true,
			},
		],
	},
	{
		key: 'organization',
		title: 'Organization',
		items: [
			{
				key: 'team',
				title: 'Team',
				href: paths.dashboard.settings.team,
				icon: 'users-three',
			},
			{
				key: 'support',
				title: 'Support',
				href: paths.dashboard.settings.support,
				icon: 'headset',
			},
			{
				key: 'cabinets',
				title: 'Cabinets',
				href: paths.dashboard.settings.cabinets,
				icon: 'lockers',
				requiresPasscodeAccess: true,
			},
			{
				key: 'taxes',
				title: 'Taxes',
				href: paths.dashboard.settings.taxes.home,
				matcher: { type: 'startsWith', href: paths.dashboard.settings.taxes.home },
				icon: 'bank',
			},
			{
				key: 'integrations',
				title: 'Apps & integrations',
				href: paths.dashboard.settings.integrations,
				matcher: { type: 'startsWith', href: paths.dashboard.settings.integrations },
				icon: 'plugs-connected',
				requiresIntegrationsAccess: true,
			},
		],
	},
] satisfies NavItemConfig[]

export const settingsNavIcons = {
	'user-circle': UserCircleIcon,
	'users-three': UsersThreeIcon,
	lockers: LockersIcon,
	headset: HeadsetIcon,
	bank: BankIcon,
	bell: BellIcon,
	'plugs-connected': PlugsConnectedIcon,
} as Record<string, Icon>

export interface SettingsSideNavListProps {
	children: ReactNode
	sx?: SxProps<Theme>
}

export function SettingsSideNavList({ children, sx }: SettingsSideNavListProps) {
	return (
		<Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0, ...sx }}>
			{children}
		</Stack>
	)
}

export interface SettingsSideNavItemProps {
	title: string
	icon?: Icon | string
	active?: boolean
	disabled?: boolean
	href?: string
	external?: boolean
	onClick?: () => void
	pathname?: string
	matcher?: NavItemConfig['matcher']
}

export function SettingsSideNavItem({
	disabled,
	external,
	href,
	icon,
	matcher,
	onClick,
	pathname,
	active,
	title,
}: SettingsSideNavItemProps) {
	const computedActive =
		active ?? (href && pathname ? isNavItemActive({ disabled, external, href, matcher, pathname }) : false)
	const IconComponent = typeof icon === 'string' ? settingsNavIcons[icon] : icon

	return (
		<Box component="li" sx={{ userSelect: 'none' }}>
			<Box
				onClick={onClick}
				{...(href && !onClick
					? {
							component: external ? 'a' : RouterLink,
							to: href,
							target: external ? '_blank' : undefined,
							rel: external ? 'noreferrer' : undefined,
						}
					: { role: 'button' })}
				sx={{
					alignItems: 'center',
					borderRadius: 1,
					color: 'var(--mui-palette-text-secondary)',
					cursor: 'pointer',
					display: 'flex',
					flex: '0 0 auto',
					gap: 1,
					p: '6px 16px',
					textDecoration: 'none',
					whiteSpace: 'nowrap',
					...(disabled && { color: 'var(--mui-palette-text-disabled)', cursor: 'not-allowed' }),
					...(computedActive && {
						bgcolor: 'var(--mui-palette-action-selected)',
						color: 'var(--mui-palette-text-primary)',
					}),
					'&:hover': {
						...(!computedActive &&
							!disabled && {
								bgcolor: 'var(--mui-palette-action-hover)',
								color: 'var(--mui-palette-text-primary)',
							}),
					},
				}}
				tabIndex={0}
			>
				{IconComponent ? (
					<Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
						<IconComponent
							fill={computedActive ? 'var(--mui-palette-text-primary)' : 'var(--mui-palette-text-secondary)'}
							fontSize="var(--icon-fontSize-md)"
							weight={computedActive ? 'fill' : undefined}
						/>
					</Box>
				) : null}
				<Box sx={{ flex: '1 1 auto' }}>
					<Typography
						component="span"
						sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
					>
						{title}
					</Typography>
				</Box>
			</Box>
		</Box>
	)
}

export function SideNav() {
	const pathname = usePathname()
	const { user } = useUser()
	const filteredItems = useFilteredNavItems(navItems)

	return (
		<div>
			<Stack
				spacing={3}
				sx={{
					flex: '0 0 auto',
					flexDirection: { xs: 'column-reverse', md: 'column' },
					position: { md: 'sticky' },
					top: '64px',
					width: { xs: '100%', md: '240px' },
				}}
				data-test="side-nav"
			>
				<Stack component="ul" spacing={3} sx={{ listStyle: 'none', m: 0, p: 0 }}>
					{filteredItems.map((group) => (
						<Stack component="li" key={group.key} spacing={2}>
							{group.title ? (
								<div>
									<Typography color="text.secondary" variant="caption">
										{group.title}
									</Typography>
								</div>
							) : null}
							<SettingsSideNavList>
								{(group.items ?? []).map((item) => (
									<SettingsSideNavItem
										key={item.key}
										disabled={item.disabled}
										external={item.external}
										href={item.href}
										icon={item.icon}
										matcher={item.matcher}
										pathname={pathname}
										title={item.title ?? item.key}
									/>
								))}
							</SettingsSideNavList>
						</Stack>
					))}
				</Stack>
				<Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
					<UserAvatar name={user?.name} src={user?.avatar} />
					<div style={{ overflowWrap: 'break-word', wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '100%' }}>
						<Typography variant="subtitle1">{user?.name}</Typography>
						<Typography color="text.secondary" variant="caption">
							{user?.email}
						</Typography>
					</div>
				</Stack>
			</Stack>
		</div>
	)
}
